import { Booking } from '@app/domain/Booking'
import { Business } from '@app/domain/Business'
import { ContextService } from '@services/context.service'
import { DTOAdapter } from '@services/DTOAdapter'
import { Dinero } from 'dinero.js'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, lastValueFrom, map, mergeMap } from 'rxjs'
import { PaymentGateway, PaymentsAccount, PaymentProviderCapability } from '@app/domain/PaymentsAccount'
import { PaymentsAccountDetails } from '@app/domain/PaymentsAccountDetails'
import { Stripe, loadStripe } from '@stripe/stripe-js'
import { environment } from '@environments/environment'
import { filterNull } from '@app/features/shared/rjxs/filter.null'
import { PaymentsAccountDetailsDTO } from './DTO/PaymentAccountDetailsDTO'
import { PaymentsAccountDTO } from './DTO/PaymentsAccountDTO'
import { BusinessDTO } from './DTO/BusinessDTO'

@Injectable({
    providedIn: 'root',
})
export class PaymentsService {

    public stripe: Promise<Stripe>

    constructor(
        private http: HttpClient,
        private dtoAdapter: DTOAdapter,
        private contextService: ContextService
    ) {
        this.stripe = lastValueFrom(
            this.contextService.business$.pipe(
                map(business => business.paymentsAccount?.usesStripe() ? business.paymentsAccount.id : null),
                filterNull(),
                mergeMap(paymentsAccountId => {
                    return this.initialiseStripe(paymentsAccountId)
                })
            )
        )
    }

    private initialiseStripe(paymentsAccountId: string) {
        return new Promise<Stripe>((resolve) => {
            resolve(
                loadStripe(environment.stripePublishableKey, {
                    stripeAccount: paymentsAccountId,
                })
                    .then((stripe) => {
                        if (!stripe) {
                            throw new Error('Stripe not loaded')
                        }
                        return stripe
                    })
            )
        })
    }

    createPaymentsAccountRegistrationLink(business: Business, gateway: PaymentGateway): Observable<string> {
        const url = new URL(`organisation/business/${encodeURIComponent(business.id)}/payments/account/registration-link`, environment.apiBaseURL)
        url.searchParams.set('gateway', gateway)
        return this.http.post<string>(url.toString(), null)
    }

    getPaymentsAccounts(business: Business): Observable<PaymentsAccount[]> {
        return this.http.get<PaymentsAccountDTO[]>(
            new URL(`organisation/business/${business.id}/payments/accounts`, environment.apiBaseURL).toString()
        ).pipe(map(accounts => accounts.map(account => this.dtoAdapter.adaptPaymentsAccountDto(account))))
    }

    createPaymentsAccount(business: Business, gateway: PaymentGateway = 'stripe'): Observable<PaymentsAccount> {
        const path = `organisation/business/${business.id}/payments/account`
        const url = new URL(path, environment.apiBaseURL)
        if (gateway !== 'stripe') url.searchParams.set('gateway', gateway)
        return this.http.post<PaymentsAccountDTO>(url.toString(), null)
            .pipe(
                map(dto => this.dtoAdapter.adaptPaymentsAccountDto(dto))
            )
    }

    createBreathepayPaymentsAccount(
        business: Business,
        clientId: string,
        apiKey: string
    ): Observable<PaymentsAccount> {
        const path = `organisation/business/${business.id}/payments/account`
        const url = new URL(path, environment.apiBaseURL)

        let queryParams = new HttpParams();
        queryParams = queryParams.set('gateway', 'breathepay');
        const body = {
            clientId,
            apiKey
        }
        const urlWithParams = queryParams.keys().length > 0 ?
            `${url.toString()}?${queryParams.toString()}` : url.toString()
        return this.http.post<PaymentsAccountDTO>(urlWithParams, body)
            .pipe(
                map(dto => this.dtoAdapter.adaptPaymentsAccountDto(dto))
            )
    }

    createRyftPaymentsAccount(
        business: Business,
        emailAddress?: string | null
    ): Observable<PaymentsAccount> {
        const path = `organisation/business/${business.id}/payments/account`
        const url = new URL(path, environment.apiBaseURL)

        let queryParams = new HttpParams()
        queryParams = queryParams.set('gateway', 'ryftpay')
        const urlWithParams = `${url.toString()}?${queryParams.toString()}`

        const body = {
            emailAddress: emailAddress ?? null,
            clientId: null,
            apiKey: null,
        }

        return this.http.post<PaymentsAccountDTO>(urlWithParams, body)
            .pipe(
                map(dto => this.dtoAdapter.adaptPaymentsAccountDto(dto))
            )
    }

    createDojoPaymentsAccount(
        business: Business,
        apiKey: string
    ): Observable<PaymentsAccount> {
        const path = `organisation/business/${business.id}/payments/account`
        const url = new URL(path, environment.apiBaseURL)
        const params = new HttpParams().set('gateway', 'dojo')
        const body = {
            clientId: null,
            apiKey,
        }
        return this.http.post<PaymentsAccountDTO>(
            `${url.toString()}?${params.toString()}`,
            body
        ).pipe(
            map(dto => this.dtoAdapter.adaptPaymentsAccountDto(dto))
        )
    }

    removePaymentsAccount(business: Business, account?: PaymentsAccount): Observable<void> {
        const path = `organisation/business/${business.id}/payments/account`
        const url = new URL(path, environment.apiBaseURL)
        const params = account ? new HttpParams().set('accountId', account.id) : new HttpParams()
        const urlWithParams = params.keys().length > 0 ? `${url.toString()}?${params.toString()}` : url.toString()
        return this.http.delete<void>(urlWithParams)
    }

    createPaymentsAccountLink(business: Business, account?: PaymentsAccount): Observable<URL> {
        const path = `organisation/business/${business.id}/payments/account/link`
        const url = new URL(path, environment.apiBaseURL)
        const params = account ? new HttpParams().set('accountId', account.id) : new HttpParams()
        const urlWithParams = params.keys().length > 0 ? `${url.toString()}?${params.toString()}` : url.toString()
        return this.http.post<URL>(urlWithParams, null)
            .pipe(
                map(dto => new URL(dto))
            )
    }

    getPaymentsAccountDetails(business: Business, account?: PaymentsAccount): Observable<PaymentsAccountDetails> {
        const path = `organisation/business/${business.id}/payments/account/details`
        const url = new URL(path, environment.apiBaseURL)
        const params = account ? new HttpParams().set('accountId', account.id) : new HttpParams()
        const urlWithParams = params.keys().length > 0 ? `${url.toString()}?${params.toString()}` : url.toString()
        return this.http.get<PaymentsAccountDetailsDTO>(urlWithParams)
            .pipe(
                map(dto => this.dtoAdapter.adaptPaymentsAccountDetailsDto(dto))
            )
    }

    assignPaymentsAccount(business: Business, capability: PaymentProviderCapability, accountId: string | null): Observable<Business> {
        const path = `organisation/business/${business.id}/payments/account-assignments/${capability}`
        return this.http.put<BusinessDTO>(new URL(path, environment.apiBaseURL).toString(), { accountId })
            .pipe(map(dto => this.dtoAdapter.adaptBusinessDto(dto)))
    }

    setApplePayEnabled(business: Business, account: PaymentsAccount, enabled: boolean): Observable<PaymentsAccount> {
        const path = `organisation/business/${business.id}/payments/account/${account.id}/apple-pay`
        const url = new URL(path, environment.apiBaseURL)
        return this.http.put<PaymentsAccountDTO>(url.toString(), { enabled })
            .pipe(
                map(dto => this.dtoAdapter.adaptPaymentsAccountDto(dto))
            )
    }

    chargeCancellationFee(
        business: Business,
        booking: Booking,
        amount: Dinero
    ): Observable<void> {
        const params = new HttpParams()
            .set('amount', amount.toUnit())
        const path = `organisation\
/business/${business.id}\
/payments\
/booking/${booking.id}\
/cancellation-fee\
?${params.toString()}`
        const url = new URL(path, environment.apiBaseURL)
        return this.http.post<void>(url.toString(), { })
    }
}

export class Payment {
    constructor(
        public clientSecret: string
    ) { }
}
